<template>
  <div :class="{ sidebar: true, hidden }">
    <div class="show-small-only">
      <div class="closebuttoncontainer">
        <button
          class="closebutton"
          @click="applyFilters"
        >
          <v-icon path="icons/close.svg" />
        </button>
      </div>
    </div>
    <div class="sidebar-title">
      <div class="sidebar-title-iconcombo">
        <v-icon
          class="sidebar-icon"
          aria-hidden="true"
          path="icons/filters.svg"
          :svg-style="{}"
        />
        <span>Filters</span>
      </div>
      <ais-stats class="show-small-only hidden">
        <template slot-scope="{ nbHits }">
          <span class="fw-bold">{{ format(nbHits) }} results</span>
        </template>
      </ais-stats>
      <a
        class="apply fw-bold show-small-only"
        @click="applyFilters"
      >Apply</a>
    </div>

    <!-- this is only here for initial filter via URL -->
    <!-- as soon as someone opens a filter block, it takes precedence -->
    <type-filter
      :type="type"
      v-if="type"
    />

    <permission-filter />

    <div class="sidebar-section">
      <h3 class="sidebar-label sidebar-label--applied">{{ currentFilterLabel }}</h3>
      <AlgoliaRefinements />
    </div>

    <div class="sidebar-section">
      <uncontrolled-collapsible :initial-expanded="type === 'restaurant' || !type">
        <template slot="header">
          <h3 class="sidebar-label">Refine dining search</h3>
        </template>
        <template slot="default">
          <div class="sidebar-body">
            <FilterPanel
              displayName="Offer type"
              icon="icons/offer.png"
              type="restaurant"
              :value="restaurant.filters.offer"
              @input="handleToggle('restaurant', 'offer')"
            >
              <DiningFilterOffer />
            </FilterPanel>

            <FilterPanel
              displayName="Party size"
              icon="icons/max-diners-filter.svg"
              type="restaurant"
              :value="restaurant.filters.party"
              @input="handleToggle('restaurant', 'party')"
            >
              <DiningFilterPartySize />
            </FilterPanel>

            <FilterPanel
              displayName="Availability"
              icon="icons/availability.svg"
              type="restaurant"
              :value="restaurant.filters.availability"
              @input="handleToggle('restaurant', 'availability')"
            >
              <DiningFilterAvailability />
            </FilterPanel>

            <FilterPanel
              displayName="Restaurant type"
              icon="icons/restaurant.svg"
              type="restaurant"
              :value="restaurant.filters.type"
              @input="handleToggle('restaurant', 'type')"
            >
              <DiningFilterRestaurantType />
            </FilterPanel>

            <FilterPanel
              displayName="Cuisine"
              icon="icons/cuisines.png"
              type="restaurant"
              :value="restaurant.filters.cuisine"
              @input="handleToggle('restaurant', 'cuisine')"
            >
              <DiningFilterCuisine />
            </FilterPanel>
          </div>
        </template>
      </uncontrolled-collapsible>
    </div>

    <div class="sidebar-section">
      <uncontrolled-collapsible :initial-expanded="type === 'cinema' || !type">
        <template slot="header">
          <h3 class="sidebar-label">Refine cinema search</h3>
        </template>
        <template slot="default">
          <div class="sidebar-body">
            <FilterPanel
              displayName="Venues"
              icon="icons/venues.svg"
              type="cinema"
              :value="cinema.filters.venues"
              @input="handleToggle('cinema', 'venues')"
            >
              <CinemaFilterVenues />
            </FilterPanel>
          </div>
        </template>
      </uncontrolled-collapsible>
    </div>
  </div>
</template>

<script>
import { createWidgetMixin } from 'vue-instantsearch';
import FilterPanel from './FilterPanel.vue';
import DiningFilterOffer from './DiningFilterOffer.vue';
import DiningFilterPartySize from './DiningFilterPartySize.vue';
import DiningFilterRestaurantType from './DiningFilterRestaurantType.vue';
import DiningFilterCuisine from './DiningFilterCuisine.vue';
import DiningFilterAvailability from './DiningFilterAvailability.vue';
import CinemaFilterVenues from './CinemaFilterVenues.vue';
import AlgoliaRefinements from './AlgoliaRefinements.vue';
import UncontrolledCollapsible from './UncontrolledCollapsible.vue';
import PermissionFilter from './PermissionFilter.vue';
import TypeFilter from './TypeFilter';
import VIcon from './VIcon';
import { store } from '../store';

const other = {
  cinema: 'restaurant',
  restaurant: 'cinema',
};

function clear(helper, type) {
  if (!helper) {
    return;
  }
  if (type === 'restaurant') {
    helper.clearRefinements('availability.available_day');
    helper.clearRefinements('offers.name');
    helper.clearRefinements('max_diners');
    helper.clearRefinements('chain');
    helper.clearRefinements('chain_name');
  } else if (type === 'cinema') {
    helper.clearRefinements('venue');
  }
}

const connectSearchType = renderFn => () => ({
  render() {
    renderFn({
      type: this.type,
    });
  },
  dispose() {},
  getWidgetSearchParameters(searchParameters, { uiState }) {
    this.type = uiState.type;
    return searchParameters;
  },
  // this connector is here to make sure type stays in the URL, we are not changing the type on expand.
  getWidgetState(uiState) {
    return { ...uiState, type: this.type };
  },
});

export default {
  mixins: [createWidgetMixin({ connector: connectSearchType })],
  components: {
    FilterPanel,
    DiningFilterOffer,
    DiningFilterPartySize,
    DiningFilterRestaurantType,
    DiningFilterCuisine,
    DiningFilterAvailability,
    CinemaFilterVenues,
    AlgoliaRefinements,
    UncontrolledCollapsible,
    PermissionFilter,
    TypeFilter,
    VIcon,
  },
  data() {
    return {
      state: store.state,
      hidden: false,
      restaurant: {
        opened: false,
        filters: {
          offer: false,
          party: false,
          availability: false,
          type: false,
          cuisine: false,
        },
      },
      cinema: {
        opened: false,
        filters: {
          venues: false,
        },
      },
    };
  },
  computed: {
    helper() {
      return this.instantSearchInstance && this.instantSearchInstance.helper;
    },
    type() {
      // read only from url, because state starts with `null`, and the value is only used on init.
      const match = location.search.match(/type=(restaurant|cinema)/i);

      if (match) {
        // first matching group, if any
        return match[1];
      }
      return undefined;
    },
    currentFilterLabel() {
      if (
        this.cinema.opened ||
        (this.type === 'cinema' && !this.restaurant.opened)
      ) {
        return 'Cinema filters applied';
      }

      if (
        this.restaurant.opened ||
        (this.type === 'restaurant' && !this.cinema.opened)
      ) {
        return 'Dining filters applied';
      }

      return 'Filters applied';
    },
  },
  methods: {
    setActiveView(activeView) {
      store.setActiveView(activeView);
    },
    handleToggle(type, name) {
      if (this[type].opened === true) {
        this[type].filters[name] = !this[type].filters[name];
      } else {
        if (
          ((this.type && this.type !== type) ||
            this.hasRefinements(other[type])) &&
          !confirm(
            `You have already selected ${other[type]} filters. By selecting a ${type} filter, you will reset all ${other[type]} filters. Are you sure you want to proceed?`
          )
        ) {
          // do nothing if user canceled
          return;
        }

        // we should remove type from url now, since it no longer matters, but other widgets
        // put it back in the URL, so we "stay in type mode"
        clear(this.helper, other[type]);
        this.reset(other[type]);

        this[type].opened = true;
        this[type].filters[name] = true;
      }
    },
    reset(type) {
      // set all filters to false
      this[type].filters = Object.keys(this[type].filters).reduce(
        (acc, curr) => {
          acc[curr] = false;
          return acc;
        },
        {}
      );

      // close this
      this[type].opened = false;
    },
    hasRefinements(type) {
      if (type === 'restaurant') {
        return (
          this.helper.hasRefinements('availability.available_day') ||
          this.helper.hasRefinements('offers.name') ||
          this.helper.hasRefinements('max_diners') ||
          this.helper.hasRefinements('chain') ||
          this.helper.hasRefinements('chain_name')
        );
      } else if (type === 'cinema') {
        return this.helper.hasRefinements('venue');
      }
      return false;
    },
    applyFilters() {
      this.hidden = true;
      //this.setActiveView('Grid');
      var actualView = window.sessionStorage.getItem('algolia_currentView');
      console.log(actualView);
      this.setActiveView(actualView ? actualView : 'Grid');
    },
    format(number) {
      if ('NumberFormat' in Intl) {
        return new Intl.NumberFormat('en-UK').format(number);
      }
      return number;
    },
  },
};
</script>

<style lang="scss" scoped>
.fw-bold {
  font-weight: 600;
}

.apply {
  display: block;
  @include theme-value(color, brand-color-one);
  text-decoration: none;
  font-size: 13px;
}

.closebuttoncontainer {
  display: flex;
  justify-content: flex-end;
}

.closebutton {
  border: none;
  margin: 0.3em 0;
  padding: 0.25em;
  height: 1.6em;
  width: 1.6em;
  box-sizing: border-box;
  background: #808080;
  color: white;
}

.show-small-only {
  display: none;

  @media (max-width: $tablet) {
    display: initial;
  }
}

.hidden {
  display: none !important;
}

.sidebar {
  width: 400px;
  margin: 0 25px;
  padding: 16px 20px;
  background: $alabaster-white;
  flex-grow: 0;
  flex-shrink: 0;
  overflow: auto;
  position: sticky;
  // header size
  height: calc(100vh - 200px);
  top: 190px;

  @media screen and (max-width: $tablet) {
    box-shadow: 0 3px 5px rgba(0, 0, 0, 0.3);
    display: block;
    margin: 0;
    width: 100vw;
    height: 100vh;
    // height: -webkit-fill-available;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
  }

  &-section {
    padding: 10px 0;
    border-bottom: 1px solid rgba($black, 0.5);
  }

  &-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 14px;
    border-bottom: 1px solid rgba($black, 0.5);
    font-size: 15px;

    &-iconcombo {
      display: flex;
      align-items: center;
    }
  }

  &-body {
    margin-bottom: 6px;
  }

  &-icon {
    display: block;
    height: 27px;
    margin-right: 9px;
  }

  &-label--applied {
    margin: 10px 0 10px;
  }

  &-label {
    font-size: 14px;
    font-weight: 700;
  }
}
</style>
